import { MutableRefObject, RefObject, useRef } from 'react'
import Konva from 'konva'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import { getContentPointer } from './getContentPointer'

type Params = {
  stageRef: RefObject<Konva.Stage | null>
  transformerRef: RefObject<Konva.Transformer | null>
  nodeRefs: MutableRefObject<Map<string, Konva.Node>>
  selectedIds: string[]
  shapes: VisualShape[]
  width: number
  height: number
  onChange: (shapes: VisualShape[]) => void
  setCursor: (cursor: string) => void
}

export const useShapeDragging = ({
  stageRef,
  transformerRef,
  nodeRefs,
  selectedIds,
  shapes,
  width,
  height,
  onChange,
  setCursor,
}: Params) => {
  const stageDragRef = useRef<{
    origin: { x: number; y: number }
    nodeStarts: Map<string, { x: number; y: number }>
    moved: boolean
  } | null>(null)

  const isInsideTransformerBounds = () => {
    const transformer = transformerRef.current
    const stage = stageRef.current
    if (!transformer || !stage || transformer.nodes().length === 0) return false
    const pos = stage.getPointerPosition()
    if (!pos) return false
    const box = transformer.getClientRect()
    return (
      pos.x >= box.x &&
      pos.x <= box.x + box.width &&
      pos.y >= box.y &&
      pos.y <= box.y + box.height
    )
  }

  const handleDragStart = () => {
    setCursor('grabbing')
  }

  const handleDragEnd = (shape: VisualShape) => () => {
    setCursor('grab')
    const node = nodeRefs.current.get(shape.id)
    if (!node) return
    onChange(
      shapes.map((s) =>
        s.id === shape.id
          ? { ...s, x: node.x() / width, y: node.y() / height }
          : s,
      ),
    )
  }

  const startStageDrag = () => {
    const pos = getContentPointer(stageRef.current)
    if (!pos) return false
    const nodeStarts = new Map<string, { x: number; y: number }>()
    selectedIds.forEach((id) => {
      const node = nodeRefs.current.get(id)
      if (node) nodeStarts.set(id, { x: node.x(), y: node.y() })
    })
    stageDragRef.current = { origin: pos, nodeStarts, moved: false }
    setCursor('grabbing')
    return true
  }

  const handleStageDragMove = () => {
    if (!stageDragRef.current) return
    const pos = getContentPointer(stageRef.current)
    if (!pos) return
    const { origin, nodeStarts } = stageDragRef.current
    const dx = pos.x - origin.x
    const dy = pos.y - origin.y
    if (dx !== 0 || dy !== 0) stageDragRef.current.moved = true
    nodeStarts.forEach((start, id) => {
      const node = nodeRefs.current.get(id)
      if (node) {
        node.x(start.x + dx)
        node.y(start.y + dy)
        node.fire('dragmove')
      }
    })
    transformerRef.current?.getLayer()?.batchDraw()
  }

  const handleStageDragEnd = () => {
    if (!stageDragRef.current) return
    if (!stageDragRef.current.moved) {
      stageDragRef.current = null
      setCursor('default')
      return
    }
    onChange(
      shapes.map((s) => {
        if (!selectedIds.includes(s.id)) return s
        const node = nodeRefs.current.get(s.id)
        if (!node) return s
        return { ...s, x: node.x() / width, y: node.y() / height }
      }),
    )
    stageDragRef.current = null
    setCursor('default')
  }

  const isStageDragging = () => stageDragRef.current !== null

  return {
    isInsideTransformerBounds,
    handleDragStart,
    handleDragEnd,
    startStageDrag,
    handleStageDragMove,
    handleStageDragEnd,
    isStageDragging,
  }
}
